import notificationSettingApi from '@/api/notification-setting'
import store from '@/store'
import { generateFormHandlerMutations } from '@/helpers/forms'
import { eventKey, saveErrorText, templateTextKey } from '@/helpers/notifications'

const getDefaultState = () => ({
  loaded: false,

  isConnected: false,
  unavailableReason: null,

  smsViaService: false,

  mobilePush: {
    isEnabled: false,
    ttlHours: 0,
    ratePerMinute: 0,
    staleTokenDays: 0,
  },
  webPush: {
    isEnabled: false,
    ttlHours: 0,
    ratePerMinute: 0,
    staleTokenDays: 0,
    siteUrl: '',
    iconUrl: '',
    firebaseApiKey: '',
    firebaseProjectId: '',
    firebaseMessagingSenderId: '',
    firebaseAppId: '',
    vapidKey: '',
  },
  sms: {
    isEnabled: false,
    sender: '',
    sandboxAllowedPhones: [],
    maxPerPhonePerDay: 0,
    maxPerHour: 0,
    journalRetentionDays: 0,
  },
  campaigns: {
    quietHoursEnabled: false,
    quietFrom: '',
    quietTo: '',
  },

  mobilePushState: null,
  webPushState: null,
  smsState: null,

  templates: [],

  events: [],
  eventsLoaded: false,
  eventsError: null,

  saving: null,

  errors: {},
})

const state = getDefaultState()

const pushPayload = settings => ({
  isEnabled: settings.isEnabled,
  ttlHours: Number(settings.ttlHours),
  ratePerMinute: Number(settings.ratePerMinute),
  staleTokenDays: Number(settings.staleTokenDays),
})

const eventPayload = event => ({
  isEnabled: event.isEnabled,
  link: event.link || null,
  sendAt: event.sendAt || null,
  radiusMeters: Number(event.radiusMeters),
  cooldownDays: Number(event.cooldownDays),
  texts: event.texts.map(({ language, title, body }) => ({ language, title, body })),
})

const sections = {
  mobilePush: {
    update: notificationSettingApi.updateMobilePush,
    payload: () => pushPayload(state.mobilePush),
  },
  webPush: {
    update: notificationSettingApi.updateWebPush,
    payload: () => ({ ...state.webPush, ...pushPayload(state.webPush) }),
  },
  sms: {
    update: notificationSettingApi.updateSms,
    payload: () => ({
      ...state.sms,
      maxPerPhonePerDay: Number(state.sms.maxPerPhonePerDay),
      maxPerHour: Number(state.sms.maxPerHour),
      journalRetentionDays: Number(state.sms.journalRetentionDays),
    }),
  },
  campaigns: {
    update: notificationSettingApi.updateCampaigns,
    payload: () => ({ ...state.campaigns }),
  },
}

const reread = (commit, mutation, payload) => notificationSettingApi.get()
  .then(({ data }) => commit(mutation, { ...payload, data }))
  .catch(() => {})

const applyStatus = (state, data) => {
  state.isConnected = data.isConnected
  state.unavailableReason = data.unavailableReason
  state.smsViaService = data.smsViaService
  state.mobilePushState = data.mobilePushState
  state.webPushState = data.webPushState
  state.smsState = data.smsState
}

const getters = {
  errorsOf: state => key => state.errors[key] || {},
}

const actions = {
  fetch({ commit, dispatch }) {
    commit('FORM_RESET')

    dispatch('fetchEvents')

    return notificationSettingApi.get()
      .then(({ data }) => commit('SETTINGS_FETCHED', data))
      .catch(() => commit('SETTINGS_FETCH_FAILED'))
  },
  async update({ commit }, section) {
    commit('SAVING', section)

    try {
      await sections[section].update(sections[section].payload())
    } catch (error) {
      commit('SAVE_FAILED', { key: section, data: error.response?.data })

      return
    }

    commit('SAVED', section)
    await reread(commit, 'SECTION_REREAD', { section })
  },
  async updateTemplate({ commit }, { code, language, body }) {
    const key = templateTextKey(code, language)
    commit('SAVING', key)

    try {
      await notificationSettingApi.updateTemplate({ code, language, body })
    } catch (error) {
      commit('SAVE_FAILED', { key, data: error.response?.data })

      return
    }

    commit('SAVED', key)
    await reread(commit, 'TEMPLATE_TEXT_REREAD', { code, language })
  },

  fetchEvents({ commit }) {
    return notificationSettingApi.getEvents()
      .then(({ data }) => commit('EVENTS_FETCHED', data))
      .catch(error => commit('EVENTS_FETCH_FAILED', error.response?.data?.message))
  },

  async updateEvent({ commit }, { code, event }) {
    const key = eventKey(code)
    commit('SAVING', key)

    try {
      await notificationSettingApi.updateEvent(code, eventPayload(event))
    } catch (error) {
      commit('SAVE_FAILED', { key, data: error.response?.data })

      return false
    }

    commit('SAVED', key)
    await notificationSettingApi.getEvents()
      .then(({ data }) => commit('EVENT_REREAD', { code, data }))
      .catch(() => {})

    return true
  },

  sendTestPush(context, customerId) {
    return notificationSettingApi.sendTestPush(customerId).then(({ data }) => data)
  },
  sendTestEvent(context, { code, customerId }) {
    return notificationSettingApi.sendTestEvent(code, customerId).then(({ data }) => data)
  },
  sendTestSms(context, phoneNumber) {
    return notificationSettingApi.sendTestSms(phoneNumber)
  },
}

const mutations = {
  SETTINGS_FETCHED(state, data) {
    applyStatus(state, data)

    if (data.isConnected) {
      Object.keys(sections).forEach(section => {
        state[section] = data[section]
      })
      state.templates = data.templates
    }

    state.loaded = true
  },
  SETTINGS_FETCH_FAILED(state) {
    state.unavailableReason = 'Не удалось прочитать настройки уведомлений.'
    state.loaded = true
  },
  SECTION_REREAD(state, { section, data }) {
    applyStatus(state, data)

    if (data.isConnected) {
      state[section] = data[section]
    }
  },
  TEMPLATE_TEXT_REREAD(state, { code, language, data }) {
    applyStatus(state, data)

    if (!data.isConnected) return

    const template = state.templates.find(x => x.code === code)
    const fresh = data.templates
      .find(x => x.code === code)
      ?.texts.find(x => x.language === language)

    if (template && fresh) {
      template.texts = template.texts.map(x => (x.language === language ? fresh : x))
    }
  },
  EVENTS_FETCHED(state, data) {
    state.events = data
    state.eventsLoaded = true
  },
  EVENTS_FETCH_FAILED(state, message) {
    state.eventsError = message || 'Не удалось прочитать автоматические уведомления.'
    state.eventsLoaded = true
  },
  EVENT_REREAD(state, { code, data }) {
    const fresh = data.find(x => x.code === code)

    if (fresh) {
      state.events = state.events.map(x => (x.code === code ? fresh : x))
    }
  },
  SAVING(state, key) {
    state.saving = key
  },
  SAVED(state, key) {
    state.saving = null
    state.errors = { ...state.errors, [key]: {} }
    store.dispatch('message/success', 'Настройки сохранены')
  },
  SAVE_FAILED(state, { key, data }) {
    state.saving = null
    state.errors = { ...state.errors, [key]: data?.validationErrors || {} }
    store.dispatch('message/error', saveErrorText(data, 'Не удалось сохранить настройки'))
  },
  FORM_RESET(state) {
    Object.assign(state, getDefaultState())
  },
  ...generateFormHandlerMutations(state),
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
}
